import React, { useState } from "react";

import { useMutation } from "@tanstack/react-query";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";

import { shareMeeting } from "services";

import { useSoilSightCurrency, useTranslation } from "utils";

import { AntCalendar, AntModal, Button, Form, Icon, Input, RuleObject, Typography, Message as message } from "components";

// ─── Types ────────────────────────────────────────────────────────────────────

interface BookingModalProps {
  isVisible: boolean;
  onClose: () => void;
}

type BookingFormValues = {
  fullName: string;
  email: string;
};

// ─── Constants ────────────────────────────────────────────────────────────────

const MORNING_SLOTS = ["09:00", "10:00", "11:00"];
const AFTERNOON_SLOTS = ["13:00", "14:00", "15:00", "16:00", "17:00", "18:00"];

const formatSelectedDate = (date: Dayjs) => date.format("MMMM D");

// ─── Sub-components ───────────────────────────────────────────────────────────

interface TimeSlotListProps {
  label: string;
  slots: string[];
  selectedTime: string | null;
  onSelect: (time: string) => void;
}

const TimeSlotList = ({ label, slots, selectedTime, onSelect }: TimeSlotListProps) => (
  <div className="booking-modal-slot-group">
    <Typography variant="paragraph-s" className="booking-modal-slot-group-label">
      {label}
    </Typography>
    {slots.map((time) => (
      <Button key={time} type={selectedTime === time ? "primary" : "secondary"} className="booking-modal-slot" onClick={() => onSelect(time)}>
        {time}
      </Button>
    ))}
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────

export const BookingModal = ({ isVisible, onClose }: BookingModalProps) => {
  const { t } = useTranslation("scenarioAnalysis");
  const { currencySymbol, isZloty } = useSoilSightCurrency();
  const consultationPrice = isZloty ? 419 : 99;
  const [form] = Form.useForm<BookingFormValues>();

  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const canConfirm = selectedDate !== null && selectedTime !== null;

  const fullNameRules: RuleObject[] = [{ required: true, message: t("upsell.booking.validation.fullNameRequired") }];

  const emailRules: RuleObject[] = [
    { required: true, message: t("upsell.booking.validation.emailRequired") },
    { type: "email", message: t("upsell.booking.validation.emailInvalid") },
  ];

  const { mutate: bookMeeting, isPending: isBooking } = useMutation({
    mutationFn: shareMeeting,
    onSuccess: () => setSubmitted(true),
    onError: () =>
      message({
        content: t("upsell.booking.error"),
        icon: "error",
      }),
  });

  const handleDateSelect = (date: Dayjs) => {
    if (date.isBefore(dayjs().startOf("day"))) return;
    setSelectedDate(date);
    setSelectedTime(null);
  };

  const handleConfirm = async () => {
    if (!canConfirm) return;
    try {
      const values = await form.validateFields();
      bookMeeting({
        clientName: values.fullName.trim(),
        clientEmail: values.email.trim(),
        date: selectedDate!.format("YYYY-MM-DD"),
        time: selectedTime!,
      });
    } catch {
      /* Ant Form displays inline field errors automatically */
    }
  };

  const handleClose = () => {
    form.resetFields();
    setSelectedDate(null);
    setSelectedTime(null);
    setSubmitted(false);
    onClose();
  };

  // Custom header renderer for Ant Calendar — matches the design's < Month Year > style
  const calendarHeaderRender = ({ value, onChange }: { value: Dayjs; onChange: (date: Dayjs) => void }) => (
    <div className="booking-modal-calendar-nav">
      <Button type="text" className="booking-modal-nav-btn" onClick={() => onChange(value.subtract(1, "month"))} aria-label={t("upsell.booking.previousMonth")}>
        <Icon iconName="tabler/chevron-left" size="s" />
      </Button>
      <span className="booking-modal-month-label">{value.format("MMMM YYYY")}</span>
      <Button type="text" className="booking-modal-nav-btn" onClick={() => onChange(value.add(1, "month"))} aria-label={t("upsell.booking.nextMonth")}>
        <Icon iconName="tabler/chevron-right" size="s" />
      </Button>
    </div>
  );

  const modalTitle = (
    <div className="booking-modal-header">
      <div className="booking-modal-expert">
        <div
          className="booking-modal-avatar"
          aria-hidden="true"
          style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/images/profile/expert-jan-hendrik.png)` }}
        />
        <div className="booking-modal-expert-info">
          <div className="booking-modal-expert-name-row">
            <span className="booking-modal-expert-name">{t("upsell.booking.expertName")}</span>
            <span className="booking-modal-expert-badge">{t("upsell.booking.expertBadge")}</span>
          </div>
          <span className="booking-modal-expert-title">{t("upsell.booking.expertTitle")}</span>
        </div>
      </div>
      <div className="booking-modal-header-right">
        <span className="booking-modal-price">{`${consultationPrice}${currencySymbol}`}</span>
        <span className="booking-modal-session">{t("upsell.booking.session")}</span>
      </div>
    </div>
  );

  const modalContent = submitted ? (
    <div className="booking-modal-confirmed">
      <Icon iconName="tabler/circle-check" size="xl" className="booking-modal-confirmed-icon" />
      <Typography variant="paragraph-m" className="booking-modal-confirmed-title">
        {t("upsell.booking.confirmedTitle")}
      </Typography>
      <Typography variant="paragraph-s" color="grey-600" className="booking-modal-confirmed-desc">
        {t("upsell.booking.confirmedDesc")} {selectedDate && formatSelectedDate(selectedDate)} {t("upsell.booking.at")} {selectedTime}.
      </Typography>
      <Button type="primary" onClick={handleClose}>
        {t("upsell.booking.close")}
      </Button>
    </div>
  ) : (
    <>
      <div className="booking-modal-body">
        {/* Left: Calendar + form */}
        <div className="booking-modal-left">
          <div className="booking-modal-calendar">
            <AntCalendar
              fullscreen={false}
              value={selectedDate ?? dayjs()}
              onSelect={handleDateSelect}
              disabledDate={(date) => date.isBefore(dayjs().startOf("day"))}
              headerRender={calendarHeaderRender}
            />
          </div>

          <Form form={form} validateTrigger={["onSubmit", "onChange"]} className="booking-modal-details">
            <Typography variant="paragraph-s" className="booking-modal-details-label">
              {t("upsell.booking.yourDetails")}
            </Typography>
            <Input
              name="fullName"
              label=""
              placeholder={t("upsell.booking.fullName")}
              rules={fullNameRules}
              validateTrigger="onChange"
              className="booking-modal-input"
              dataTestId="booking-modal-full-name"
            />
            <Input
              name="email"
              label=""
              placeholder={t("upsell.booking.email")}
              rules={emailRules}
              validateTrigger="onChange"
              className="booking-modal-input"
              dataTestId="booking-modal-email"
            />
          </Form>
        </div>

        {/* Right: time slots */}
        <div className="booking-modal-right">
          {selectedDate ? (
            <>
              <Typography variant="paragraph-m" className="booking-modal-selected-date">
                {formatSelectedDate(selectedDate)}
              </Typography>
              <div className="booking-modal-slots">
                <TimeSlotList label={t("upsell.booking.morning")} slots={MORNING_SLOTS} selectedTime={selectedTime} onSelect={setSelectedTime} />
                <TimeSlotList label={t("upsell.booking.afternoon")} slots={AFTERNOON_SLOTS} selectedTime={selectedTime} onSelect={setSelectedTime} />
              </div>
            </>
          ) : (
            <div className="booking-modal-no-date">
              <Typography variant="paragraph-s" color="grey-600" className="booking-modal-no-date-text">
                {t("upsell.booking.selectDate")}
                <br />
                {t("upsell.booking.toSeeSlots")}
              </Typography>
            </div>
          )}
        </div>
      </div>

      <div className="booking-modal-footer">
        <Typography variant="paragraph-s" color="grey-600" className="booking-modal-footer-hint">
          {t("upsell.booking.pickDateTime")}
        </Typography>
        <Button type="primary" disabled={!canConfirm || isBooking} loading={isBooking} onClick={handleConfirm} className="booking-modal-confirm-btn">
          {t("upsell.booking.confirmAndPay", { price: consultationPrice, currency: currencySymbol })}
        </Button>
      </div>
    </>
  );

  return (
    <AntModal
      open={isVisible}
      onCancel={handleClose}
      footer={null}
      width={560}
      centered
      className="booking-modal-ant"
      data-testid="booking-modal"
      title={modalTitle}
    >
      {modalContent}
    </AntModal>
  );
};

export default BookingModal;
